// Apple Home Screen Layout payload types
// Reference: https://developer.apple.com/documentation/devicemanagement/homescreenlayout

// IconKind is a Smplify extension that records whether a Web Clip has a real
// site favicon or should render the generic globe fallback. Persisting this
// alongside the clip keeps the canvas visually stable across page refreshes
// and layout export/import, independent of the in-memory favicon cache.
export type IconKind = "favicon" | "generic";

export type IconItem =
  | { Type: "Application"; BundleID: string; DisplayName?: string }
  // Icon is a Smplify extension: a resolved favicon URL persisted with the
  // clip so the editor can render the recognizable site glyph without
  // re-resolving it on every render. Apple's payload ignores unknown keys.
  | {
      Type: "WebClip";
      URL: string;
      DisplayName?: string;
      Icon?: string;
      IconKind?: IconKind;
    }
  | { Type: "Folder"; DisplayName: string; Pages: IconItem[][] };

export interface HomeScreenLayout {
  Pages: IconItem[][];
  Dock?: IconItem[];
}

export type Target = "iphone" | "ipad" | "both";

export interface SmplifyExtension {
  target: Target;
  variants: {
    iphone?: HomeScreenLayout;
    ipad?: HomeScreenLayout;
  };
}

// What we read/write through onConfigChange
export interface HomeScreenConfig extends HomeScreenLayout {
  "x-smplify"?: SmplifyExtension;
}

// Form-factor specs (Apple + Addigy reference)
export const SPECS = {
  iphone: {
    label: "iPhone",
    grid: { cols: 4, rows: 6 },
    dockMax: 4,
    folderGrid: { cols: 3, rows: 3 },
  },
  ipad: {
    label: "iPad",
    grid: { cols: 6, rows: 5 },
    dockMax: 6,
    folderGrid: { cols: 4, rows: 4 },
  },
} as const;

export const MAX_HOME_PAGES = 10;
export const MAX_FOLDER_PAGES = 15;

export type FormFactor = "iphone" | "ipad";

export function pageCapacity(ff: FormFactor) {
  return SPECS[ff].grid.cols * SPECS[ff].grid.rows;
}

export function folderPageCapacity(ff: FormFactor) {
  return SPECS[ff].folderGrid.cols * SPECS[ff].folderGrid.rows;
}
